import { action, computed, observable, reaction, runInAction } from "mobx";

import EventViewStore from "../event/EventViewStore";

import {
	BettingType,
	EventKey,
	ProviderNameEnum,
	SpecifierType,
} from "@gp/models";

import { SportsStatsApiService } from "@api-services/sport-stats";
import { AdditionalOfferViewStore } from "../shared/AdditionalOfferViewStore";
import {
	IPeriodStatistic,
	IPeriodStatisticsChartData,
	StatsRequestModel,
	IEventStatsBase,
	StatusCodes,
	IHomeAwayTeams,
	MatchStatAvailabilityIndex,
	IMatchStatsAvailability,
	IGroupedPeriodStatisticsChartData,
	StreakStatsAvailability,
	StreakStatsRequestModel,
	StreakStatsResponse,
	RequestType,
	RecentForm,
	ArticleInsightsData,
} from "@data-types/sport-stats/SportStatsTypes";
import { EventOffer, getSortedEventKeysBySpecifierValue } from "@gp/offer";
import PressureIndexStore from "./PressureIndexStore";
import { Subscription } from "rxjs";
import { statisticsKeys } from "@features/offer/shared/statistics/chartConfig";
import { SportAbrv, getCurrentLanguage } from "@utils";
import { LoaderStore } from "@state/stores/common";

export default class SportStatisticsStore {
	eventStore: EventViewStore | AdditionalOfferViewStore;
	pressureIndex: PressureIndexStore;
	loader: LoaderStore = new LoaderStore();

	availableBTypesSet = observable.set(new Set(), {
		name: "availableBTypesSet",
	});

	@observable matchStatisticsRawData: IPeriodStatistic[] | null;
	@observable matchStatisticsChartData: IPeriodStatisticsChartData[] = [];
	@observable
	groupedMatchStatisticsChartData: IGroupedPeriodStatisticsChartData[] = [];

	@observable isMatchStatActive = false;
	@observable isStreakStatsActive = false;
	@observable isGoalDistributionActive = false;
	@observable isFullStatActive = false;
	@observable winLostDrawStats: Map<string, RecentForm[]> | null = null;
	@observable betRadarStats: string | null | undefined = null;

	@observable isFetching = false;
	@observable isMatchStatsAvailable = false;
	@observable streakStats: StreakStatsResponse | null = null;
	@observable streakStatsAvailability: StreakStatsAvailability | null = null;
	@observable isError = false;

	@observable eventInsights: string[] = [];

	@observable articleInsights: ArticleInsightsData | null = null;
	@observable streakFilter: string = "all";
	@observable matchFilter: string = "all";

	private dataSubscription?: Subscription | null = null;
	private availabilitySubscription?: Subscription | null = null;

	private matchOverview = [
		"ballPossession",
		"expectedGoals",
		"bigChanceCreated",
		"totalShotsOnGoal",
		"goalkeeperSaves",
		"cornerKicks",
		"fouls",
		"passes",
		"totalTackle",
		"freeKicks",
		"yellowCards",
		"penalties",
		"redCards",
		"offsides",
	];

	private shots = [
		"totalShotsOnGoal",
		"shotsOnGoal",
		"shotsOffGoal",
		"hitWoodwork",
		"blockedScoringAttempt",
		"totalShotsInsideBox",
		"totalShotsOutsideBox",
		"freeKicks",
	];

	private attack = [
		"bigChanceScored",
		"bigChanceMissed",
		"accurateThroughBall",
		"touchesInOppBox",
		"fouledFinalThird",
		"offsides",
		"dangerousAttack",
	];

	private passes = [
		"accuratePasses",
		"throwIns",
		"finalThirdPhaseStatistic",
		"finalThirdEntries",
		"accurateLongBalls",
		"accurateCross",
		"passesOwnHalf",
		"passes",
	];

	private duels = [
		"duelWonPercent",
		"dispossessed",
		"groundDuelsPercentage",
		"aerialDuelsPercentage",
		"dribblesPercentage",
	];

	private defending = [
		"wonTacklePercent",
		"totalTackle",
		"interceptionWon",
		"ballRecovery",
		"totalClearance",
		"blocks",
	];

	private goalkeeping = ["goalkeeperSaves", "goalsPrevented", "goalKicks"];

	private statisticsSoccerCategories = [
		{
			title: "EVENT.STATISTICS.TITLES.MATCH_OVERVIEW",
			data: this.matchOverview,
		},
		{ title: "EVENT.STATISTICS.TITLES.SHOTS", data: this.shots },
		{ title: "EVENT.STATISTICS.TITLES.ATTACK", data: this.attack },
		{ title: "EVENT.STATISTICS.TITLES.PASSES", data: this.passes },
		{ title: "EVENT.STATISTICS.TITLES.DUELS", data: this.duels },
		{ title: "EVENT.STATISTICS.TITLES.DEFENDING", data: this.defending },
		{
			title: "EVENT.STATISTICS.TITLES.GOALKEEPING",
			data: this.goalkeeping,
		},
	];

	constructor(eventStore: EventViewStore | AdditionalOfferViewStore) {
		this.eventStore = eventStore;
		this.pressureIndex = new PressureIndexStore();

		reaction(
			() => this.isMatchStatActive,
			(isActive) => {
				if (!this.eventStore.event?.liveStatus) {
					return;
				}
				if (isActive) {
					this.disposeAvailabilitySubscription();
				} else {
					this.subscribeToAvailability();
				}
			}
		);
	}

	@computed
	get homeAndAwayTeam(): IHomeAwayTeams | null {
		const eventData = this.getEventData();
		if (eventData) {
			const homeTeam = this.eventStore.lookupsStore?.teams?.get(
				eventData.event.teamOneId || ""
			);
			const awayTeam = this.eventStore.lookupsStore?.teams?.get(
				eventData.event.teamTwoId || ""
			);

			if (homeTeam && awayTeam) {
				return {
					homeTeam: homeTeam.name,
					awayTeam: awayTeam.name,
				};
			}
		}
		return null;
	}

	@action.bound
	mapSoccerStatChartData() {
		this.groupedMatchStatisticsChartData = [];

		this.statisticsSoccerCategories.forEach((category) => {
			this.groupedMatchStatisticsChartData.push({
				title: category.title,
				data: this.transformStatisticsData(category.data),
			});
		});
	}

	@action.bound
	transformStatisticsData(group: Array<string>) {
		if (this.matchStatisticsRawData != null) {
			return this.matchStatisticsRawData
				.filter((item) => item.key && group.includes(item.key))
				.map((item) => {
					const {
						teamOneValue = 0,
						teamTwoValue = 0,
						key,
						teamOneDisplayValue,
						teamTwoDisplayValue,
						statisticsType,
						isPercentage = false,
					} = item;
					const total = isPercentage
						? 100
						: teamOneValue + teamTwoValue;

					return {
						infoSection: `EVENT.STATISTICS.MATCH_STATS.${key}.INFO`,
						title: `EVENT.STATISTICS.MATCH_STATS.${key}.TITLE`,
						key: statisticsKeys[key] || null,
						data: {
							home: teamOneDisplayValue,
							away: teamTwoDisplayValue,
							homeValue: teamOneValue,
							awayValue: teamTwoValue,
							statisticsType,
						},
						max: total,
					};
				})
				.sort(
					(a, b) => group.indexOf(a.title) - group.indexOf(b.title)
				);
		}
		return [];
	}

	@action.bound
	toggleMatchStat() {
		this.matchFilter = "all";
		this.isMatchStatActive = !this.isMatchStatActive;
	}

	@action.bound
	toggleFullStat() {
		if (this.isFullStatActive) {
			this.betRadarStats = null;
		} else {
			const eventData = this.getEventData();
			this.betRadarStats = eventData?.event.source?.matchId;
		}
		this.isFullStatActive = !this.isFullStatActive;
	}

	@action.bound
	toggleStreakStats() {
		if (this.isStreakStatsActive) {
			this.clearStreakStats();
			this.streakFilter = "all";
		} else {
			this.fetchStreakStats(RequestType.Data);
		}

		this.isStreakStatsActive = !this.isStreakStatsActive;
	}

	@action.bound
	toggleGoalDistribution() {
		if (this.isGoalDistributionActive) {
			this.clearStreakStats();
		} else {
			this.fetchStreakStats(RequestType.Data);
		}

		this.isGoalDistributionActive = !this.isGoalDistributionActive;
	}

	@computed
	get isStreakStatAvailable(): boolean {
		const data = this.streakStatsAvailability;
		if (!data) {
			return false;
		} else {
			return data.headToHead || data.general;
		}
	}

	@computed
	get isGoalDistributionAvailable(): boolean {
		const data = this.streakStatsAvailability;
		if (!data) {
			return false;
		} else {
			return (
				data.goalDistribution?.homeTeam ||
				data.goalDistribution?.awayTeam
			);
		}
	}

	@computed
	get isFullStatsAvailable(): boolean {
		const eventData = this.getEventData();
		const isBREvent =
			eventData?.event.source?.providerName === ProviderNameEnum.BetRadar;
		if (!isBREvent) {
			return false;
		}
		return true;
	}

	@computed
	get availableStreakFilterOptions() {
		const options = [
			{ key: "all", title: "EVENT.STATISTICS.PLAYER_STATS.ALL" },
			{
				key: "competitionAverages",
				title: "EVENT.STATISTICS.STREAK_STATS.TITLE_COMPETITION_AVERAGES",
			},
			{
				key: "headToHead",
				title: "EVENT.STATISTICS.STREAK_STATS.TITLE_HEAD_TO_HEAD",
			},
			{
				key: "general",
				title: "EVENT.STATISTICS.STREAK_STATS.TITLE_STREAKS",
			},
			{
				key: "goalDistribution",
				title: "EVENT.STATISTICS.GOAL_DISTRIBUTION.TITLE",
			},
		];

		if (!this.streakStatsAvailability) return options;

		return options.filter((option) => {
			switch (option.key) {
				case "competitionAverages":
					return this.streakStatsAvailability?.competitionAverages;
				case "headToHead":
					return this.streakStatsAvailability?.headToHead;
				case "general":
					return this.streakStatsAvailability?.general;
				case "goalDistribution":
					return (
						this.streakStatsAvailability?.goalDistribution
							?.homeTeam ||
						this.streakStatsAvailability?.goalDistribution?.awayTeam
					);
				default:
					return true;
			}
		});
	}

	@computed
	get filteredStreakStats() {
		if (!this.streakStats) return null;

		const data = this.streakStats.data;

		switch (this.streakFilter) {
			case "competitionAverages":
				return {
					data: { competitionAverages: data.competitionAverages },
				};
			case "headToHead":
				return { data: { headToHead: data.headToHead } };
			case "general":
				return { data: { general: data.general } };
			case "goalDistribution":
				return { data: { goalDistribution: data.goalDistribution } };
			case "all":
			default:
				return { data };
		}
	}

	@computed
	get availableMatchStatsFilterOptions() {
		const options = [];

		if (
			(this.pressureIndex.values &&
				this.pressureIndex.values.length > 0) ||
			(this.groupedMatchStatisticsChartData &&
				this.groupedMatchStatisticsChartData.some(
					(g) => g.data && g.data.length > 0
				))
		) {
			options.push({
				key: "all",
				title: "EVENT.STATISTICS.PLAYER_STATS.ALL",
			});
		}

		if (this.pressureIndex.values && this.pressureIndex.values.length > 0) {
			options.push({
				key: "pressureIndex",
				title: "EVENT.STATISTICS.PRESSURE_INDEX_TITLE",
			});
		}

		this.statisticsSoccerCategories.forEach((category) => {
			const group = this.groupedMatchStatisticsChartData.find(
				(g) => g.title === category.title
			);
			if (group && group.data && group.data.length > 0) {
				options.push({
					key: category.title,
					title: category.title,
				});
			}
		});

		return options;
	}

	@computed
	get filteredMatchStats() {
		if (this.matchFilter === "all") {
			return {
				pressureIndex: this.pressureIndex.values,
				groupedMatchStatisticsChartData:
					this.groupedMatchStatisticsChartData,
			};
		}
		if (this.matchFilter === "pressureIndex") {
			return { pressureIndex: this.pressureIndex.values };
		}
		const category = this.groupedMatchStatisticsChartData.filter(
			(c) => c.title === this.matchFilter
		);
		return category ? { groupedMatchStatisticsChartData: category } : {};
	}

	@action.bound
	createMatchStatsFilter<T extends "data" | "availability">(
		requestType: T
	): { RequestType: T; Events: IEventStatsBase[] } | null {
		const filter = this.generatePressureIndexEventFilter();
		if (!filter) {
			return null;
		}

		return {
			RequestType: requestType,
			Events: [filter],
		};
	}

	@action.bound
	async fetchMatchStatsAvailability() {
		const filter = this.createMatchStatsFilter("availability");

		if (!filter) {
			return null;
		}

		try {
			return await SportsStatsApiService.fetchMatchStatistics({
				specific: filter,
			});
		} catch (error) {
			console.error(error);
			return null;
		}
	}

	createStreakStatsRequest(
		requestType: RequestType.Data | RequestType.Availability
	): StreakStatsRequestModel | null {
		const eventData = this.getEventData();

		if (!eventData || !eventData.event.id) {
			console.warn(
				"EventId is required to create a streak stats request."
			);
			return null;
		}

		return {
			specific: {
				RequestType: requestType,
				EventId: eventData.event.id,
				TournamentId: eventData.tournament.id,
			},
		};
	}

	async fetchStreakStats(type: RequestType.Data | RequestType.Availability) {
		const filter = this.createStreakStatsRequest(type);

		if (!filter) {
			console.warn("Invalid filter for streak stats.");
			return;
		}

		if (type === RequestType.Data) {
			this.loader.suspend();
		}

		try {
			const response =
				await SportsStatsApiService.fetchStreakStatistics(filter);

			runInAction(() => {
				if (response.statusCode === 2000000000 && response.response) {
					if (type === RequestType.Data) {
						this.streakStats = response
							.response[0] as unknown as StreakStatsResponse;
					} else if (type === RequestType.Availability) {
						const data = response.response[0].data;

						this.streakStatsAvailability = {
							headToHead: Boolean(data?.headToHead),
							general: Boolean(data?.general),
							goalDistribution: {
								homeTeam: Boolean(
									data?.goalDistribution?.homeTeam
								),
								awayTeam: Boolean(
									data?.goalDistribution?.awayTeam
								),
							},
							competitionAverages: Boolean(
								data?.competitionAverages
							),
						};
					}
				} else {
					console.warn(
						`Unexpected response or missing data. Status: ${response.statusCode}`,
						response
					);
					this.resetStreakStats(type);
				}
			});
		} catch (error) {
			console.error(error);
			runInAction(() => this.resetStreakStats(type));
		} finally {
			if (type === RequestType.Data) {
				runInAction(() => {
					this.loader.resume();
				});
			}
		}
	}

	private resetStreakStats(
		type: RequestType.Data | RequestType.Availability
	) {
		if (type === RequestType.Data) {
			this.streakStats = null;
		} else if (type === RequestType.Availability) {
			this.streakStatsAvailability = null;
		}
	}

	@action.bound
	clearStreakStats() {
		this.resetStreakStats(RequestType.Data);
	}

	@action.bound
	setStreakFilter(streakFilter: string) {
		this.streakFilter = streakFilter;
	}

	@action.bound
	setMatchFilter(matchFilter: string) {
		this.matchFilter = matchFilter;
	}

	checkStreakStatsAvailability() {
		if (!WEBPACK_IS_SPORT_STATISTICS_ENABLED) {
			return;
		}

		if (this.eventStore.sport?.abrv != "soccer") {
			return;
		}

		this.fetchStreakStats(RequestType.Availability);
	}

	@action.bound
	async fetchMatchStats() {
		this.isFetching = true;

		const filter = this.createMatchStatsFilter("data");
		if (filter == null) {
			return;
		}

		try {
			const matchStats = await SportsStatsApiService.fetchMatchStatistics(
				{
					specific: filter,
				}
			);

			if (matchStats.statusCode == StatusCodes.NOT_AVAILABLE) {
				throw new Error("Live statistics not available.");
			}

			runInAction(() => {
				if (
					!matchStats?.response?.length ||
					!matchStats.response?.[0].data
				) {
					throw new Error("Live statistics not available.");
				}

				if (matchStats.response != null) {
					this.isMatchStatsAvailable = true;
					const response = matchStats.response[0];

					if (
						response.data.pressureIndex != null &&
						response.data.pressureIndex.length
					) {
						if (
							response.data.pressureIndex[
								response.data.pressureIndex.length - 1
							].minute >
							this.pressureIndex.getLastMinuteReceived()
						)
							this.pressureIndex.processMessage(
								response.data.pressureIndex
							);
					}

					if (response.data.statistics != null) {
						this.matchStatisticsRawData =
							response.data.statistics.statisticItems;
						this.mapSoccerStatChartData();
					}
					this.isError = false;
				}
			});
		} catch (error) {
			console.error(error);
			this.isError = true;
			this.isMatchStatsAvailable = false;
		} finally {
			this.isFetching = false;
		}
	}

	@action.bound
	async initializeLiveMatchStatistics() {
		this.disposeDataSubscription();

		const filter = this.generateSubscriptionFilter("data");

		if (!filter) {
			return;
		}

		this.dataSubscription = this.eventStore.rootStore.sportStatHub
			?.getSubscription(filter)
			.subscribe({
				next: (data: any) => {
					if (Array.isArray(data) && data.length > 0) {
						if (
							data[0].data &&
							Array.isArray(data[0].data.pressureIndex)
						) {
							this.pressureIndex.processMessage(
								data[0].data.pressureIndex
							);
						}
						if (
							data[0].data &&
							data[0].data.statistics?.statisticItems.length > 0
						) {
							this.matchStatisticsRawData =
								data[0].data.statistics.statisticItems;

							this.mapSoccerStatChartData();
						}
					}
				},
				error: (e: any) => {
					console.log(e);
					this.isError = true;
				},
			});

		await this.fetchMatchStats();

		this.mapSoccerStatChartData();
	}

	generateSubscriptionFilter(requestType: "data" | "availability") {
		if (this.isError) {
			return;
		}

		const eventFilter = this.generatePressureIndexEventFilter();

		if (!eventFilter) {
			return;
		}

		return {
			language: getCurrentLanguage(),
			subscriptionId:
				requestType == "data"
					? `sport-stats-${eventFilter.EventId}`
					: `sport-stats-availability-${eventFilter.EventId}`,
			environment: WEBPACK_ENVIRONMENT_KEY,
			typeAbrv: "Live",
			filterSpecific: {
				RequestType: requestType,
				Events: [
					{
						...eventFilter,
						LastMinuteReceived:
							this.pressureIndex.getLastMinuteReceived(),
					},
				],
			},
		};
	}

	generatePressureIndexEventFilter(): IEventStatsBase | null {
		const eventData = this.getEventData();

		if (!eventData || !eventData.event.id) {
			console.warn("Unable to create filter, missing event data");
			return null;
		}

		return {
			EventId: eventData.event.id,
		};
	}

	@action.bound
	disposeDataSubscription() {
		if (this.dataSubscription != null) {
			this.dataSubscription.unsubscribe();
			this.dataSubscription = null;
		}
	}

	@action.bound
	disposeAvailabilitySubscription() {
		if (this.availabilitySubscription != null) {
			this.availabilitySubscription.unsubscribe();
			this.availabilitySubscription = null;
		}
	}

	@action.bound
	async checkLiveStatisticsAvailability() {
		if (!WEBPACK_IS_SPORT_STATISTICS_ENABLED) {
			return;
		}

		if (this.eventStore.sport?.abrv != "soccer") {
			return;
		}

		if (
			!this.eventStore.event?.liveStatus &&
			!this.eventStore?.isOfferHistory
		) {
			return;
		}

		this.disposeAvailabilitySubscription();

		this.pressureIndex.init(this.eventStore.sport?.abrv);

		const isLive = this.eventStore.event?.liveStatus;

		// first send http request for availability
		if (!this.isMatchStatsAvailable) {
			const response = await this.fetchMatchStatsAvailability();

			if (
				response &&
				response.statusCode !== StatusCodes.NOT_AVAILABLE &&
				response?.response?.[0]?.data?.statistics ==
					MatchStatAvailabilityIndex.AVAILABLE
			) {
				if (isLive) {
					this.startLiveMatchStatSession();
				} else {
					await this.fetchMatchStats();
				}
			}
		}

		// then subscribe to availability
		if (isLive) {
			this.subscribeToAvailability();
		}
	}

	@action.bound
	subscribeToAvailability() {
		const filter = this.generateSubscriptionFilter("availability");

		if (!filter) {
			return;
		}

		// subscribe to availability only if stats are closed and on event page opening
		if (!this.isMatchStatActive) {
			this.availabilitySubscription =
				this.eventStore.rootStore.sportStatHub
					?.getSubscription(filter)
					.subscribe({
						next: (hubData: any) => {
							const response =
								hubData[0] as IMatchStatsAvailability;
							if (
								response?.data?.statistics &&
								response?.data?.statistics ==
									MatchStatAvailabilityIndex.AVAILABLE
							) {
								this.startLiveMatchStatSession();
								return;
							}
							this.isMatchStatsAvailable = false;
						},
						error: (e: any) => {
							console.log(e);
							this.isMatchStatsAvailable = false;
							this.isError = true;
						},
					});
		}
	}

	startLiveMatchStatSession() {
		if (!this.isMatchStatsAvailable) {
			this.isMatchStatsAvailable = true;
			this.initializeLiveMatchStatistics();
		}
	}

	//#endregion Match stats

	//#region Betting types stats

	@action.bound
	async fetchBettingStats(
		bettingType: BettingType,
		offerKey: Parameters<
			SportStatisticsStore["getSpecificBettingTypeProperties"]
		>[1],
		playerIds?: (string | undefined)[]
	) {
		try {
			const filter = this.createBettingTypeStatusRequest(
				RequestType.Data,
				bettingType,
				offerKey,
				playerIds
			);
			if (!filter) return null;
			// @ts-expect-error
			return offerKey?.bettingType?.specifierType == SpecifierType.PLAYER
				? (
						await SportsStatsApiService.fetchPlayerTypeStatistics(
							filter
						)
					).response
				: (
						await SportsStatsApiService.fetchTeamBettingTypeStatistics(
							filter
						)
					).response;
		} catch (e) {
			console.error("Error", e);
			return null;
		}
	}

	// EVENT PAGE TODO: fix types
	@action.bound
	async fetchWinLostDrawStats(event: EventOffer) {
		if (!WEBPACK_IS_SPORT_STATISTICS_ENABLED) {
			return;
		}
		if (this.eventStore.sport?.abrv != SportAbrv.SOCCER) {
			return;
		}
		try {
			const tournament = this.eventStore.lookupsStore.tournaments.get(
				event.tournamentId
			);

			if (!tournament) {
				throw new Error("Tournament info is not found");
			}

			const filter = {
				specific: {
					RequestType: "data",
					BettingTypes: ["3way"],
					Tournament: tournament.abrv,
					EventId: event.id,
					...this.getSpecificBettingTypeProperties([], null),
				},
			};

			const response =
				await SportsStatsApiService.fetchTeamBettingTypeStatistics(
					// @ts-expect-error
					filter
				);

			if (response.statusCode === 2000000000) {
				const winLostDrawStats = response.response.reduce(
					(acc, entry) => {
						acc.set(
							entry.teamAbrv,
							// @ts-expect-error
							entry.data.recentForm as RecentForm[]
						);
						return acc;
					},
					new Map<string, RecentForm[]>()
				);

				runInAction(() => {
					this.winLostDrawStats = winLostDrawStats;
				});
			} else {
				throw response;
			}
		} catch (error) {
			// output error when debugging
			console.warn("Could not fetch 3way stats");
		}
	}

	getPlayerName(playerId: string): string | null {
		const player = this.eventStore.lookupsStore.players.get(playerId);
		return player ? `${player.firstName}-${player.lastName}` : null;
	}

	getEventData() {
		const store = this.eventStore;
		if (store.event && store.tournament) {
			return {
				event: store.event,
				tournament: store.tournament,
			};
		}
		return null;
	}

	createBettingTypeStatusRequest(
		requestType: RequestType,
		bettingType: BettingType | string[],
		offerKey: Parameters<
			SportStatisticsStore["getSpecificBettingTypeProperties"]
		>[1],
		playerIds?: (string | undefined)[],
		isPlayerStat?: boolean
	): StatsRequestModel | null {
		const eventData = this.getEventData();
		if (!eventData) {
			return null;
		}
		const bettingTypes = Array.isArray(bettingType)
			? bettingType
			: [bettingType.abrv];

		const commonRequest = {
			RequestType: requestType,
			BettingTypes: bettingTypes,
			Tournament: eventData.tournament.abrv,
			EventId: eventData.event.id,
		};

		let players: string[] = [];

		if (playerIds) {
			for (const playerId of playerIds) {
				if (playerId) {
					const playerName = this.getPlayerName(playerId);
					if (playerName !== null) {
						players.push(playerName.toLowerCase());
					}
				}
			}
		}

		// @ts-expect-error
		return offerKey?.bettingType?.specifierType == SpecifierType.PLAYER ||
			isPlayerStat
			? {
					specific: {
						...commonRequest,
						Players: players,
					},
				}
			: {
					specific: {
						...commonRequest,
						...this.getSpecificBettingTypeProperties(
							bettingType,
							offerKey
						),
					},
				};
	}

	async fetchAvailableBettingStats() {
		const bettingTypesAbrvArr: string[] = [];
		const playerBettingTypesAbrvArr: string[] = [];
		const playerIds: string[] = [];

		this.eventStore.lookupsStore.bettingTypes.forEach((val: any) => {
			if (val.specifierType == SpecifierType.PLAYER) {
				playerBettingTypesAbrvArr.push(val.abrv);
			} else {
				bettingTypesAbrvArr.push(val.abrv);
			}
		});

		this.eventStore.lookupsStore.players.forEach((player: any) => {
			playerIds.push(player.id);
		});

		if (bettingTypesAbrvArr.length > 0) {
			const filter = this.createBettingTypeStatusRequest(
				RequestType.Availability,
				bettingTypesAbrvArr,
				null
			);

			if (filter == null) {
				return;
			}

			try {
				const response =
					await SportsStatsApiService.fetchTeamBettingTypeStatistics(
						filter
					);
				runInAction(() => {
					/* Error code for no response */
					if (response.statusCode == 4040020000) {
						console.warn("No available statistics for this event");
						return;
					}

					Object.keys(response.response[0].data).forEach((bt) => {
						if (
							response.response[0].data[bt] ||
							response.response[1].data[bt]
						) {
							this.availableBTypesSet.add(bt);
						}
					});
				});
			} catch (error) {
				console.error(error);
			}
		}

		if (playerBettingTypesAbrvArr.length > 0) {
			const filter = this.createBettingTypeStatusRequest(
				RequestType.Availability,
				playerBettingTypesAbrvArr,
				null,
				playerIds,
				true
			);
			if (filter == null) {
				return;
			}

			try {
				const availabilityResponse =
					await SportsStatsApiService.fetchPlayerTypeStatistics(
						filter
					);

				runInAction(() => {
					if (availabilityResponse.statusCode == 4040020000) {
						console.warn(
							"No available player statistics for this event"
						);
					} else {
						Object.keys(
							availabilityResponse.response[0].data
						).forEach((bt) => {
							if (
								availabilityResponse.response[0].data[bt] ||
								availabilityResponse.response[1].data[bt]
							) {
								this.availableBTypesSet.add(bt);
							}
						});
					}
				});
			} catch (error) {
				console.error(error);
			}
		}
	}

	getSpecificBettingTypeProperties(
		bettingType: BettingType | string[],
		offerKey:
			| { id: string; tip: string; value: number }
			| { id: string; tip: string; value: number }[]
			| null
	) {
		if (Array.isArray(bettingType)) {
			return {};
		}
		switch (bettingType.abrv) {
			case "over-under":
				let bettingTypeKeys =
					//@ts-expect-error
					getSortedEventKeysBySpecifierValue(offerKey);
				const concatSpecifiers = bettingTypeKeys
					.map((bt: EventKey) => bt.specifier?.total)
					.join(",");
				return { OverUnderThresholds: concatSpecifiers };

			case "3way":
				if (!offerKey || Array.isArray(offerKey)) {
					return {};
				}

				const keys = this.eventStore.keyOffersMap.get(offerKey.id);
				if (keys && keys.size < 3) {
					return {};
				}
				let specific3way = {
					HomeWinOdds: 0,
					DrawOdds: 0,
					HomeLossOdds: 0,
				};
				keys?.forEach((key) => {
					switch (key.tip) {
						case "1":
							specific3way.HomeWinOdds = key.value;
							break;
						case "X":
							specific3way.DrawOdds = key.value;
							break;
						case "2":
							specific3way.HomeLossOdds = key.value;
							break;
					}
				});

				return specific3way;
			default:
				return {};
		}
	}

	checkBettingTypeStatisticsAvailability() {
		if (!WEBPACK_IS_SPORT_STATISTICS_ENABLED) {
			return;
		}

		if (this.eventStore.sport?.abrv != SportAbrv.SOCCER) {
			return;
		}

		this.fetchAvailableBettingStats();
	}
	//#endregion Betting types stats

	//#region Event Insights

	@computed
	get isEventInsightsAvailable() {
		return this.eventInsights.length > 0 || this.articleInsights != null;
	}

	checkEventInsightsAvailability() {
		if (!WEBPACK_IS_SPORT_STATISTICS_ENABLED) {
			return;
		}

		if (this.eventStore.sport?.abrv != SportAbrv.SOCCER) {
			return;
		}

		this.fetchEventInsightsAvailability();

		this.fetchArticleInsights();
	}

	async fetchArticleInsights() {
		if (!this.eventStore.eventId) {
			return;
		}

		try {
			const articleInsights =
				await SportsStatsApiService.fetchArticleInsights(
					this.eventStore.eventId
				);

			if (articleInsights.response) {
				runInAction(() => {
					this.articleInsights = articleInsights.response;
				});
			}
		} catch (error) {
			console.error("Article insights unavailable: ", error);
			this.articleInsights = null;
		}
	}

	async fetchEventInsightsAvailability() {
		if (!this.eventStore.eventId) {
			return;
		}
		try {
			const availabilityResponse =
				await SportsStatsApiService.fetchEventInsights({
					specific: {
						RequestType: RequestType.Availability,
						EventId: this.eventStore.eventId,
						Language: getCurrentLanguage(),
					},
				});

			if (availabilityResponse.statusCode != StatusCodes.AVAILABLE) {
				return;
			}

			const isAvailable =
				availabilityResponse.response.filter((insight) => {
					return (
						insight.data.insights ==
						MatchStatAvailabilityIndex.AVAILABLE
					);
				}).length > 0;

			if (isAvailable) {
				const eventInsightsDataResponse =
					await SportsStatsApiService.fetchEventInsights({
						specific: {
							RequestType: RequestType.Data,
							EventId: this.eventStore.eventId,
							Language: getCurrentLanguage(),
						},
					});

				if (
					eventInsightsDataResponse.statusCode !=
					StatusCodes.AVAILABLE
				) {
					return;
				}

				const eventInsightsData = eventInsightsDataResponse.response
					.map((insight) => {
						return insight?.data?.insights || [];
					})
					.flat();

				runInAction(() => {
					this.eventInsights = eventInsightsData;
				});
			}
		} catch (error) {
			console.log("Event insights unavailable: ", error);
		}
	}

	//#endregion Event Insights

	@action.bound
	onDispose() {
		this.matchStatisticsRawData = [];
		this.matchStatisticsChartData = [];
		this.eventInsights = [];
		this.isFetching = false;
		this.isMatchStatsAvailable = false;
		this.availableBTypesSet.clear();
		this.isError = false;
		this.disposeDataSubscription();
		this.disposeAvailabilitySubscription();
		this.pressureIndex?.onDispose();
		this.streakStatsAvailability = null;
		this.streakStats = null;
		this.winLostDrawStats = null;
		this.articleInsights = null;
	}
}
